import { useState } from 'react';
import { supabase, verifyPassword } from '../../utils/supabase';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (user: { email: string; full_name: string; role: string }) => void;
}

export default function LoginModal({ isOpen, onClose, onLogin }: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error || !data) {
        setError('Invalid email or password. Please try again.');
        return;
      }

      // Check if password is hashed (starts with $2a$ or $2b$ for bcrypt)
      let isPasswordValid = false;
      
      if (data.password.startsWith('$2a$') || data.password.startsWith('$2b$')) {
        // Password is hashed, verify with bcrypt
        isPasswordValid = await verifyPassword(password, data.password);
      } else {
        // Password is plain text, compare directly
        isPasswordValid = password === data.password;
      }
      
      if (!isPasswordValid) {
        setError('Invalid email or password. Please try again.');
        return;
      }

      // Check if user account is active
      if (data.status === 'inactive' || !data.is_active) {
        setError('Your account has been deactivated. Please contact your administrator.');
        return;
      }

      // Store user info in localStorage
      localStorage.setItem('currentUser', JSON.stringify(data));
      onLogin(data);
      onClose();
      setEmail('');
      setPassword('');
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Staff Login</h2>
        
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">{error}</div>
          )}

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-blue-50 rounded text-sm">
          <div className="flex items-center mb-2">
            <i className="ri-information-line text-blue-600 mr-2"></i>
            <strong className="text-blue-800">Staff Access</strong>
          </div>
          <p className="text-blue-700">
            Use your assigned email and password to access the admin dashboard. 
            Contact your administrator if you need assistance.
          </p>
        </div>
      </div>
    </div>
  );
}
